import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import { portalUrl } from '../config/site';
import { productGroups } from '../config/products';
import { exitPreview, usePreviewMode, useSiteContent } from '../content/siteContent';

const legalLinks = [
  ['/terms-of-service', 'Terms and Conditions'],
  ['/privacy-policy', 'Privacy Policy'],
  ['/cookie-policy', 'Cookie Policy'],
  ['/acceptable-use-policy', 'Acceptable Use'],
  ['/fair-use-policy', 'Fair Use'],
  ['/emergency-calls', 'Emergency Calls'],
  ['/complaints-code', 'Complaints Code'],
  ['/data-processing', 'Data Processing'],
];

// The cookie banner (CookieConsent.jsx) only runs on the website build, where Google tags are loaded.
const hasConsentBanner = () => typeof window !== 'undefined' && typeof window.gtag === 'function';

// Forget the saved choice and reload, so the banner asks again (see the Cookie Policy).
const reopenCookieChoices = () => {
  try { localStorage.removeItem('voip2_consent'); } catch (e) { /* storage blocked: nothing was saved */ }
  window.location.reload();
};

// Shown while an admin is looking at draft content from the CMS preview link.
const PreviewBar = () => (
  <div role="status" className="fixed left-1/2 top-[4.25rem] z-[70] flex -translate-x-1/2 items-center gap-3 rounded-full bg-[#c8ff00] px-5 py-2.5 text-sm font-semibold text-[#0a1f2e] shadow-lg">
    Preview: showing unpublished content
    <button type="button" onClick={exitPreview} className="rounded-full bg-[#0a1f2e] px-3 py-1 text-xs text-white hover:bg-black">
      Exit preview
    </button>
  </div>
);

const Footer = () => {
  const c = useSiteContent('global');
  const preview = usePreviewMode();
  return (
    <footer className="bg-[#0a1f2e] text-white">
      {preview && <PreviewBar />}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <div className="text-2xl font-bold mb-4">
              VOIP<span style={{ color: '#c8ff00' }}>2</span>
            </div>
            <p className="text-gray-300 text-sm mb-4">
              {c('footer.blurb')}
            </p>
            <div className="flex items-center space-x-2 text-sm text-gray-300 mb-2">
              <Mail className="w-4 h-4" />
              <span>{c('contact.email')}</span>
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-300 mb-2">
              <Phone className="w-4 h-4" />
              <span>{c('contact.phone')}</span>
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-300">
              <MapPin className="w-4 h-4" />
              <span>{c('contact.address_street')}, {c('contact.address_locality')}</span>
            </div>
          </div>

          {/* Products */}
          <div>
            <h2 className="text-lg font-semibold mb-4">Products</h2>
            <div className="space-y-5">
              {productGroups.map((group) => (
                <div key={group.id}>
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500">{group.title}</p>
                  <ul className="space-y-2">
                    {group.items.map((product) => (
                      <li key={product.to}><Link to={product.to} className="text-gray-300 hover:text-[#c8ff00] text-sm transition-colors">{product.title}</Link></li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Coverage & Compliance */}
          <div>
            <h2 className="text-lg font-semibold mb-4">Coverage & Compliance</h2>
            <ul className="space-y-2">
              <li><Link to="/coverage" className="text-gray-300 hover:text-[#c8ff00] text-sm transition-colors">Coverage & Pricing</Link></li>
              <li><Link to="/regulatory-requirements" className="text-gray-300 hover:text-[#c8ff00] text-sm transition-colors">Regulations & Requirements</Link></li>
              <li><Link to="/resources" className="text-gray-300 hover:text-[#c8ff00] text-sm transition-colors">Resources</Link></li>
              <li><Link to="/resources?type=user_guide" className="text-gray-300 hover:text-[#c8ff00] text-sm transition-colors">User guides</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h2 className="text-lg font-semibold mb-4">Company</h2>
            <ul className="space-y-2">
              <li><Link to="/about" className="text-gray-300 hover:text-[#c8ff00] text-sm transition-colors">About Us</Link></li>
              <li><Link to="/contact" className="text-gray-300 hover:text-[#c8ff00] text-sm transition-colors">Contact Us</Link></li>
              <li><a href={portalUrl('/customer/login')} className="text-gray-300 hover:text-[#c8ff00] text-sm transition-colors">Customer Portal</a></li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 mt-8 pt-8 flex flex-col lg:flex-row lg:justify-between gap-4">
          <div className="text-sm text-gray-400">
            <p>© {new Date().getFullYear()} VOIP2 Limited. All rights reserved.</p>
            <p className="mt-1 text-xs text-gray-500">
              Registered in England and Wales, company number 16487711. Registered office: 60 Copthorne Avenue,
              Ilford, England, IG6 2SQ. ICO registration number ZC139352. Ofcom Routing Identifier (RID) SMW.
            </p>
          </div>
          <nav aria-label="Legal" className="flex flex-wrap gap-x-5 gap-y-2 lg:justify-end lg:max-w-xl">
            {legalLinks.map(([to, label]) => (
              <Link key={to} to={to} className="text-sm text-gray-400 hover:text-[#c8ff00] transition-colors">{label}</Link>
            ))}
            {hasConsentBanner() && (
              <button type="button" onClick={reopenCookieChoices}
                className="text-sm text-gray-400 hover:text-[#c8ff00] transition-colors">Cookie settings</button>
            )}
          </nav>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
